import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Saml2ServiceProviderFormDrawerProps, Saml2ServiceProviderFormDrawerState } from "./Saml2ServiceProviderFormDrawer.types";
import React from "react";
import { Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle } from "../Drawer";
import { createSaml2ServiceProviderAsync, findSaml2ServiceProviderAsync, getCancelTokenSource, Saml2ServiceProviderModel, updateSaml2ServiceProviderAsync } from "src/api";
import { Loader, UnstyledButton } from "src/ui";
import { localize } from "src/l10n";
import { Icon, PrimaryButton, TextField } from "@fluentui/react";
import { FormControl, FormSection } from "src/ui/components/Forms";
import { Conditional } from "../Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { SmallBody } from "../Text";

/**
 * A form drawer for creating or editing a SAML2 service provider.  If the 
 * `serviceProviderId` is not provided, the panel will display a creation form 
 * rather than an edit form.
 * 
 * @param param0 The properties of the component.
 * @returns The element to render.
 */
function Saml2ServiceProviderFormDrawer({
    onClose,
    open,
    serviceProviderId,
}: Saml2ServiceProviderFormDrawerProps): ReactElement {
    const isNew = !serviceProviderId;

    const [state, setState] = useState<Saml2ServiceProviderFormDrawerState>({
        loadingState: "idle",
    });
    const [model, setModel] = useState<Saml2ServiceProviderModel>({
        name: "",
        entityId: "",
        assertionConsumerServiceUrls: [""],
        metadataUrl: "",
        singleLogoutServiceUrl: "",
    });

    const onOpenChanged = useCallback(
        (open: boolean) => {
            if (open || !onClose) {
                return;
            }

            onClose();
        },
        [onClose]);

    const closeButton = useMemo<JSX.Element>(
        () => (
            <UnstyledButton
                className="close-button"
                title={localize("Stang")}
                onClick={onClose}
            >
                <Icon iconName="ChromeClose" />
            </UnstyledButton>
        ),
        [onClose]);

    const onTextFieldChanged = useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
            const target = event.currentTarget;
            if (!target.name.includes('[')) {
                setModel((m) => ({ ...m, [target.name]: newValue }));
                return;
            }

            const [fieldName, index] = target.name.split("[", 2);
            const arrayField = fieldName as keyof Saml2ServiceProviderModel;
            const arrayIndex = parseInt(index.replace("]", ""), 10);
            if (isNaN(arrayIndex)) {
                return;
            }

            setModel((m) => {
                const array = (m[arrayField] || []) as any[];
                const newArray = [...array];
                newArray[arrayIndex] = newValue;

                return { ...m, [arrayField]: newArray };
            });
        },
        [setModel]);

    const onAddAcsUrlClicked = useCallback(
        () => setModel((m) => ({
            ...m,
            assertionConsumerServiceUrls: [...m.assertionConsumerServiceUrls, ""],
        })),
        [setModel]);

    const onSubmitting = useCallback(
        async () => {
            setState({ loadingState: "submitting" });

            try {
                const promise = isNew
                    ? createSaml2ServiceProviderAsync(model)
                    : updateSaml2ServiceProviderAsync(serviceProviderId, model);

                await promise;
            }
            catch (err) {
                console.error(err);
                setState({ loadingState: "idle" });

                return;
            }
            
            setState({ loadingState: "idle" });
            onClose();
        },
        [serviceProviderId, isNew, model, setState]);

    useEffect(function _Saml2ServiceProviderFormDrawerMount() {
        if (!open || !serviceProviderId) {
            return;
        }

        const cancelTokenSource = getCancelTokenSource();

        setState({ loadingState: "loading" });
        findSaml2ServiceProviderAsync(serviceProviderId, cancelTokenSource.token)
            .then(function _Saml2ServiceProviderFormDrawerFindSP(serviceProvider) {
                setModel({ ...serviceProvider });
                setState({ loadingState: "idle" });
            })
            .catch(function _Saml2ServiceProviderFormDrawerFindSPError(error) {
                console.error(error);

                onClose();
            });

        return function _Saml2ServiceProviderFormDrawerUnmount() {
            cancelTokenSource.cancel();
        };
    }, [open,setModel, setState, onClose]);

    return (
        <Drawer position="end" onOpenChange={onOpenChanged} open={open} size="medium">
            <DrawerHeader>
                <DrawerHeaderTitle action={closeButton}>
                    {isNew
                        ? localize("CREATE_SAML_SERVICE_PROVIDER")
                        : localize("EDIT_SAML_SERVICE_PROVIDER")}
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody>
                <Conditional condition={state.loadingState !== "idle"}>
                    <SpintrLoader />
                </Conditional>

                <Conditional condition={state.loadingState === "idle"}>
                    <FormSection>
                        <FormControl>
                            <TextField
                                aria-required={true}
                                className="textField"
                                label={localize("Namn")}
                                name="name"
                                onChange={onTextFieldChanged}
                                required={true}
                                validateOnFocusIn={true}
                                validateOnFocusOut={true}
                                value={model.name || ""}
                            />
                        </FormControl>
                    </FormSection>
                    
                    <FormSection>
                        <FormControl>
                            <TextField
                                aria-required={true}
                                className="textField"
                                label={localize("SAML_IDENTIFIER")}
                                name="entityId"
                                onChange={onTextFieldChanged}
                                required={true}
                                validateOnFocusIn={true}
                                validateOnFocusOut={true}
                                value={model.entityId || ""}
                            />
                        </FormControl>
                    </FormSection>
                    
                    <FormSection>
                        <FormControl>
                            <TextField
                                aria-required={true}
                                className="textField"
                                label={localize("METADATA_URL")}
                                name="metadataUrl"
                                onChange={onTextFieldChanged}
                                required={true}
                                validateOnFocusIn={true}
                                validateOnFocusOut={true}
                                value={model.metadataUrl || ""}
                            />
                        </FormControl>
                    </FormSection>
                    
                    <FormSection>
                        <FormControl label={localize("SAML_SSO_SERVICE_URLS")}>
                            {model.assertionConsumerServiceUrls.map((url, index) => (<>
                                <TextField
                                    className="textField"
                                    key={index}
                                    name={`assertionConsumerServiceUrls[${index}]`}
                                    onChange={onTextFieldChanged}
                                    validateOnFocusIn={true}
                                    validateOnFocusOut={true}

                                    pattern="^(https?://.*)$"
                                    value={url || ""}
                                />
                                <Conditional condition={model.assertionConsumerServiceUrls.length > 1}>
                                    <UnstyledButton
                                        className="removeButton"
                                        onClick={() => setModel((m) => {
                                            const newArray = [...m.assertionConsumerServiceUrls];
                                            newArray.splice(index, 1);

                                            return { ...m, assertionConsumerServiceUrls: newArray };
                                        })}
                                    >
                                        <Icon iconName="Delete" />
                                    </UnstyledButton>
                                </Conditional>
                            </>))}
                            <PrimaryButton
                                className="addButton"
                                disabled={state.loadingState === "submitting"}
                                onClick={onAddAcsUrlClicked}                                
                            >
                                {localize("LaggTill")}
                            </PrimaryButton>
                        </FormControl>
                    </FormSection>
                    
                    <FormSection>
                        <FormControl>
                            <TextField
                                aria-required={true}
                                className="textField"
                                label={localize("SAML_SLO_SERVICE_URL")}
                                name="singleLogoutServiceUrl"
                                onChange={onTextFieldChanged}
                                required={true}
                                validateOnFocusIn={true}
                                validateOnFocusOut={true}
                                value={model.singleLogoutServiceUrl || ""}
                            />
                        </FormControl>
                    </FormSection>
                </Conditional>

            </DrawerBody>

            <DrawerFooter>
                <Conditional condition={state.loadingState !== "loading"}>
                    <UnstyledButton onClick={onClose} disabled={state.loadingState === "submitting"}>
                        <SmallBody color="contentLight" weight="medium">
                            {localize("Avbryt")}
                        </SmallBody>
                    </UnstyledButton>
                    <PrimaryButton
                        className="PlannerItemFormDrawer-button PlannerItemFormDrawer-saveButton"
                        disabled={state.loadingState === "submitting"}
                        onClick={onSubmitting}    
                    >
                        {localize("Publicera")}
                    </PrimaryButton>
                </Conditional>
            </DrawerFooter>
        </Drawer>
    );
}

export default Saml2ServiceProviderFormDrawer;
